import type { Candidate, CandidateStage } from '../../types';
import { getInterviewDay, getInterviewRoundRank } from './candidateInterviewUtils';

export type CandidateListItem = Candidate;

export type SortDirection = 'asc' | 'desc';
export type SortKey =
  | 'candidate'
  | 'position'
  | 'source'
  | 'channel'
  | 'interviewSchedule'
  | 'interviewResult'
  | 'interviewRound'
  | 'stage'
  | 'score'
  | 'appliedDate';

export type SortConfig = {
  direction: SortDirection;
  key: SortKey;
};

export const candidateSortKeys: SortKey[] = [
  'candidate',
  'position',
  'source',
  'channel',
  'interviewSchedule',
  'interviewResult',
  'interviewRound',
  'stage',
  'score',
  'appliedDate',
];

export function isSortKey(value: string | undefined): value is SortKey {
  return Boolean(value && candidateSortKeys.includes(value as SortKey));
}

/** Normalize raw phone input to Vietnamese national format starting with 0 (digits only). */
function normalizeVnPhoneDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.startsWith('84')) {
    const rest = digits.slice(2);
    const subscriber = rest.startsWith('0') ? rest : `0${rest}`;
    return subscriber.slice(0, 11);
  }

  if (digits.startsWith('0')) {
    return digits.slice(0, 11);
  }

  if (digits.length === 9 && /^[35789]/.test(digits)) {
    return `0${digits}`;
  }

  return digits;
}

/** Normalize phone for CSV export: Vietnamese national format starting with 0. */
export function formatVnPhoneForCsv(raw: string): string {
  return normalizeVnPhoneDigits(raw);
}

/** Format phone for on-screen display: national format grouped as 0XXX XXX XXX. */
export function formatVnPhoneForDisplay(raw: string): string {
  const digits = normalizeVnPhoneDigits(raw);
  if (digits.length !== 10) return raw;
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
}

export function escapeCsvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export const filterCandidates = (
  candidates: CandidateListItem[],
  filters: {
    search: string;
    interviewDayFilter?: string;
    roleFilters: string[];
    stageFilters: string[];
    teamFilters?: string[];
  },
) => {
  const normalizedSearch = filters.search.trim().toLowerCase();
  const teamFilters = filters.teamFilters ?? [];

  return candidates.filter(
    (candidate) =>
      (normalizedSearch.length === 0 ||
        candidate.name.toLowerCase().includes(normalizedSearch) ||
        candidate.email.toLowerCase().includes(normalizedSearch) ||
        candidate.phone.toLowerCase().includes(normalizedSearch)) &&
      (filters.roleFilters.length === 0 || filters.roleFilters.includes(candidate.jdId)) &&
      (filters.stageFilters.length === 0 || filters.stageFilters.includes(candidate.stage)) &&
      (teamFilters.length === 0 ||
        (candidate.department != null && teamFilters.includes(candidate.department))) &&
      (!filters.interviewDayFilter ||
        getInterviewDay(candidate.nextInterviewAt) === filters.interviewDayFilter),
  );
};

const compareNullable = <T extends string | number>(
  left: T | null | undefined,
  right: T | null | undefined,
) => {
  const leftMissing = left === null || left === undefined || left === '';
  const rightMissing = right === null || right === undefined || right === '';

  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const stageOrder: Record<CandidateStage, number> = {
  New: 1,
  Screening: 2,
  Interview: 3,
  Offer: 4,
  Hired: 5,
  Rejected: 6,
};

const getCandidateSortValue = (candidate: CandidateListItem, key: SortKey) => {
  switch (key) {
    case 'candidate':
      return candidate.name;
    case 'position':
      return candidate.role;
    case 'source':
      return candidate.source ?? '';
    case 'channel':
      return candidate.channel ?? '';
    case 'interviewSchedule':
      return candidate.nextInterviewAt ? new Date(candidate.nextInterviewAt).getTime() : null;
    case 'interviewResult':
      return candidate.nextInterviewResult ?? '';
    case 'interviewRound':
      return getInterviewRoundRank(candidate.nextInterviewRound);
    case 'stage':
      return stageOrder[candidate.stage];
    case 'score':
      return candidate.score ?? null;
    case 'appliedDate':
      return candidate.appliedDate ? new Date(candidate.appliedDate).getTime() : null;
    default:
      return '';
  }
};

export const sortCandidates = (candidates: CandidateListItem[], sortConfig: SortConfig) =>
  [...candidates].sort((left, right) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const result = compareNullable(
      getCandidateSortValue(left, sortConfig.key),
      getCandidateSortValue(right, sortConfig.key),
    );

    if (result !== 0) return result * direction;
    return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
  });
